<script setup lang="ts">
import {
  computed,
  onActivated,
  onDeactivated,
  onMounted,
  onUnmounted,
  ref,
} from "vue";
import {
  isNothing,
  just,
  matchMaybe,
  nothing,
  toMaybe,
  type Maybe,
} from "../../lib/maybe";
import { storeToRefs } from "pinia";
import { useRouter } from "vue-router";
import { listen, type UnlistenFn } from "@tauri-apps/api/event";
import {
  trashChapterContext,
  trashKind,
  trashKindLabel,
  trashLabel,
} from "../../lib/deletedFiles";
import { formatChangeWhen } from "../../lib/fileHistory";
import { isDemoRoot } from "../../lib/platform";
import { isTauri } from "../../lib/runtime";
import { listDeletedFiles, type DeletedFileInfo } from "../../lib/tauri/git";
import { trashPath } from "../../router/projectPaths";
import { useEditorSessionStore } from "../../stores/useEditorSessionStore";
import { useOutlineActionsStore } from "../../stores/useOutlineActionsStore";
import { useProjectWorkspaceStore } from "../../stores/useProjectWorkspaceStore";
import ZwButton from "../ui/ZwButton.vue";

const session = useEditorSessionStore();
const workspace = useProjectWorkspaceStore();
const outlineActions = useOutlineActionsStore();
const router = useRouter();
const { rootPath, trashRestoringPath } = storeToRefs(session);

const trashItems = ref<DeletedFileInfo[]>([]);
const trashBusy = ref(false);

/** Stay on Maybe. Do not peel session.trashPreview to null for local state. */
const trashPreviewPath = computed((): Maybe<string> =>
  matchMaybe(session.trashPreview, {
    just: (preview): Maybe<string> => just(preview.path),
    nothing: (): Maybe<string> => nothing,
  }),
);

function isUsableProjectRoot(root: Maybe<string>): boolean {
  return matchMaybe(root, {
    just: (path) => path.length > 0 && (isTauri() || isDemoRoot(path)),
    nothing: () => false,
  });
}

function isRestoringBusy(): boolean {
  return matchMaybe(trashRestoringPath.value, {
    just: (path) => path.length > 0,
    nothing: () => false,
  });
}

function isRestoringItem(path: string): boolean {
  return matchMaybe(trashRestoringPath.value, {
    just: (restoring) => restoring === path,
    nothing: () => false,
  });
}

function isPreviewActive(path: string): boolean {
  return matchMaybe(trashPreviewPath.value, {
    just: (preview) => preview === path,
    nothing: () => false,
  });
}

function trashKindText(path: string): string {
  const kind = trashKind(path);
  if (isNothing(kind)) return "";
  return trashKindLabel(kind.value);
}

function trashChapterContextText(path: string): string {
  return matchMaybe(trashChapterContext(path), {
    just: (chapter) => chapter,
    nothing: () => "",
  });
}

async function refreshTrash(): Promise<void> {
  const root = rootPath.value;
  if (isUsableProjectRoot(root) === false || isNothing(root)) {
    trashItems.value = [];
    return;
  }
  trashBusy.value = true;
  try {
    trashItems.value = await listDeletedFiles(root.value);
  } catch (error) {
    console.error("Failed to list trash", error);
    trashItems.value = [];
  } finally {
    trashBusy.value = false;
  }
}

function onRestoreDeleted(item: DeletedFileInfo): void {
  if (isRestoringBusy() === true) return;
  void workspace.onRestoreDeleted(item.relativePath, item.oid);
}

async function onPreviewDeleted(item: DeletedFileInfo): Promise<void> {
  // Route sync owns loading the deleted blob (and blanking the live file).
  await router.push(trashPath(just(item.relativePath)));
}

let unlistenCommitted: Maybe<UnlistenFn> = nothing;

function registerOutlineChrome(): void {
  outlineActions.refreshTrash = just(refreshTrash);
}

function clearOutlineChrome(): void {
  outlineActions.refreshTrash = nothing;
}

onMounted(() => {
  void refreshTrash();
  registerOutlineChrome();
  if (isTauri() === false) return; // Tauri commit events only
  void listen<{ root?: string }>("project-committed", (event) => {
    const eventRoot = toMaybe(event.payload?.root);
    const sessionRoot = rootPath.value;
    if (
      isUsableProjectRoot(eventRoot) === false ||
      isUsableProjectRoot(sessionRoot) === false ||
      isNothing(eventRoot) ||
      isNothing(sessionRoot)
    ) {
      return;
    }
    if (eventRoot.value !== sessionRoot.value) return;
    void refreshTrash();
  }).then((fn) => {
    unlistenCommitted = just(fn);
  });
});

onActivated(() => {
  registerOutlineChrome();
  void refreshTrash();
});
onDeactivated(clearOutlineChrome);

onUnmounted(() => {
  clearOutlineChrome();
  matchMaybe(unlistenCommitted, {
    just: (fn) => {
      fn();
    },
    nothing: () => {},
  });
  unlistenCommitted = nothing;
});

defineExpose({ refreshTrash });
</script>

<template>
  <nav class="zw-outline-nav zw-scroll zw-outline-nav-trash" aria-label="Trash">
    <div v-if="trashBusy && trashItems.length === 0" class="zw-outline-empty">
      <p>Loading…</p>
    </div>
    <div v-else-if="trashItems.length === 0" class="zw-outline-empty">
      <p>Nothing in the trash. Deleted chapters and notes show up here.</p>
    </div>
    <ul v-else class="trash-list">
      <li
        v-for="item in trashItems"
        :key="`${item.relativePath}-${item.oid}`"
        class="trash-item"
        :class="{ 'trash-item-active': isPreviewActive(item.relativePath) }"
      >
        <button
          type="button"
          class="trash-item-copy min-w-0"
          :title="`Preview ${item.relativePath}`"
          @click="onPreviewDeleted(item)"
        >
          <span class="trash-item-title">
            {{ trashLabel(item.relativePath) }}
          </span>
          <span
            v-if="trashChapterContextText(item.relativePath).length > 0"
            class="trash-item-chapter"
          >
            {{ trashChapterContextText(item.relativePath) }}
          </span>
          <span class="trash-item-meta">
            <template v-if="trashKindText(item.relativePath).length > 0">
              {{ trashKindText(item.relativePath) }}
              <span aria-hidden="true">·</span>
            </template>
            {{ formatChangeWhen(item.time) }}
          </span>
        </button>
        <ZwButton
          size="sm"
          variant="primary"
          class="trash-restore"
          :disabled="isRestoringBusy()"
          @click="onRestoreDeleted(item)"
        >
          {{ isRestoringItem(item.relativePath) ? "Restoring…" : "Restore" }}
        </ZwButton>
      </li>
    </ul>
  </nav>
</template>

<style scoped>
.zw-outline-nav {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 0 var(--space-sm) var(--space-md);
}

.zw-outline-nav-trash {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.zw-outline-empty {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-sm);
  margin: var(--space-sm) var(--space-sm) 0;
  font-size: 0.9rem;
  line-height: 1.4;
  color: color-mix(in oklch, var(--text-muted) 85%, transparent);
}

.zw-outline-empty p {
  margin: 0;
}

.trash-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.trash-item {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.35rem 0.4rem;
  border: 1px solid var(--line);
  border-radius: 0.25rem;
  background: var(--bg);
}

.trash-item-active {
  border-color: color-mix(in oklch, var(--color-zw-amber) 45%, var(--line));
  background: color-mix(in oklch, var(--color-zw-amber) 12%, var(--bg));
}

.trash-item-copy {
  appearance: none;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  flex: 1 1 auto;
  min-width: 0;
  text-align: left;
  cursor: pointer;
  border-radius: 0.2rem;
}

.trash-item-copy:hover .trash-item-title {
  color: var(--color-zw-amber);
}

.trash-item-copy:focus-visible {
  outline: 2px solid var(--color-zw-amber);
  outline-offset: 2px;
}

.trash-item-title {
  display: block;
  margin: 0;
  font-size: 0.88rem;
  font-weight: 500;
  color: var(--text-strong);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.trash-item-chapter {
  display: block;
  margin: 0.1rem 0 0;
  font-size: 0.78rem;
  color: var(--text-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.trash-item-meta {
  display: block;
  margin: 0.1rem 0 0;
  font-size: 0.72rem;
  color: var(--text-muted);
}

.trash-restore {
  flex-shrink: 0;
}
</style>
